/**
 * Validator Registry for DRC and ERC Checks
 *
 * This module provides a centralized configuration for all Design Rule Check (DRC)
 * and Electrical Rule Check (ERC) validators. It manages validator enablement via
 * feature flags and provides a factory function to generate validators based on
 * the current feature flag configuration.
 *
 * DRC validators check PCB layout rules (e.g., overlapping copper, airwires, trace corner radius).
 * ERC validators check schematic electrical rules (e.g., pin-to-pin compatibility, floating pins, driver conflicts).
 *
 * Validator visibility can be controlled via LaunchDarkly feature flags:
 * - DRC validators are enabled by default (unless explicitly disabled)
 * - ERC validators are disabled by default (require explicit enablement)
 *
 * @see https://app.launchdarkly.com/flux-app/production/features/drc-enabled-checkers
 */
import { areWeTestingWithJest } from "@buildwithflux/shared";
import { 
// ERC validators (schematic electrical checks)
ErcSinglePinNetValidator, ErcNoConnectValidator, ErcDriverConflictValidator, ErcFloatingPinValidator, ErcPinToPinValidator, ErcShortedComponentsValidator, ErcSimilarLabelsValidator, ErcUndrivenNetValidator, ErcDifferentUnitFootprintValidator, ErcDifferentUnitNetValidator, ErcDifferentUnitValueValidator, ErcSimilarLabelAndPowerValidator, ErcSimilarPowerValidator, ErcPowerPinValidator, ErcDuplicateReferenceValidator, ErcUnannotatedValidator, ErcUnconnectedWireEndpointValidator, ErcWireDanglingValidator, 
// DRC validators (PCB layout checks)
OverlappingTracesValidator, AirwiresValidator, OverlappingCopperValidator, FloatingCopperValidator, InvalidFillsValidator, InvalidLayerValidator, ProtectedIntrusionsValidator, TraceCornerRadiusValidator, ImportantOverrideValidator, DeprecatedRulesValidator, 
// Manufacturing checks (in UI)
MissingFootprintsValidator, ManufacturePartNumberValidator, } from "./validators";
/**
 * Registry of all DRC and ERC validators with their enablement configuration.
 *
 * DRC validators (PCB layout checks):
 * - Enabled by default unless explicitly disabled via feature flag
 * - Check for issues like overlapping copper, airwires, invalid layers, etc.
 *
 * ERC validators (schematic electrical checks):
 * - Disabled by default, require explicit enablement via feature flag
 * - Check for issues like pin-to-pin compatibility, floating pins, driver conflicts, etc.
 */
const validatorRegistry = {
    // ============================================
    // DRC Validators (PCB Layout Checks)
    // Enabled by default unless explicitly disabled
    // Some validators can run on backend when BackendPcbRuntimeStructures are provided
    // ============================================
    OverlappingTracesValidator: {
        isEnabled: (enabled) => enabled === true,
        createValidator: () => new OverlappingTracesValidator(),
        supportsBackend: true, // Only needs baked pcbLayoutNodes
        requiresRuntimeStructures: true, // Needs baked nodes with bakedRules
    },
    OverlappingCopperValidator: {
        isEnabled: (enabled) => enabled !== false && areWeTestingWithJest() === false,
        createValidator: () => new OverlappingCopperValidator(),
        supportsBackend: true,
        requiresRuntimeStructures: true,
    },
    MissingFootprintsValidator: {
        isEnabled: (enabled) => enabled !== false,
        createValidator: () => new MissingFootprintsValidator(),
        supportsBackend: true, // Only needs elements
    },
    ManufacturePartNumberValidator: {
        isEnabled: (enabled) => enabled !== false,
        createValidator: () => new ManufacturePartNumberValidator(),
        supportsBackend: true, // Only needs elements and pcbLayoutNodes
    },
    InvalidLayerValidator: {
        isEnabled: (enabled) => enabled !== false,
        createValidator: () => new InvalidLayerValidator(),
        supportsBackend: true, // Only needs baked pcbLayoutNodes
        requiresRuntimeStructures: true, // Needs baked nodes with bakedRules.stackup
    },
    ImportantOverrideValidator: {
        isEnabled: (enabled) => enabled !== false,
        createValidator: () => new ImportantOverrideValidator(),
        supportsBackend: true, // Only needs pcbLayoutRuleSets and pcbLayoutNodes (raw, not baked)
    },
    AirwiresValidator: {
        isEnabled: (enabled) => enabled !== false,
        createValidator: () => new AirwiresValidator(),
        supportsBackend: true,
        requiresRuntimeStructures: true,
    },
    DeprecatedRulesValidator: {
        isEnabled: (enabled) => enabled !== false,
        createValidator: () => new DeprecatedRulesValidator(),
        supportsBackend: true, // Only needs pcbLayoutRuleSets and pcbLayoutNodes (raw, not baked)
    },
    InvalidFillsValidator: {
        isEnabled: (enabled) => enabled !== false,
        createValidator: () => new InvalidFillsValidator(),
        supportsBackend: true, // Only needs baked pcbLayoutNodes
        requiresRuntimeStructures: true, // Needs baked nodes with bakedRules
    },
    FloatingCopperValidator: {
        isEnabled: (enabled) => enabled !== false,
        createValidator: () => new FloatingCopperValidator(),
        supportsBackend: true,
        requiresRuntimeStructures: true,
    },
    TraceCornerRadiusValidator: {
        isEnabled: (enabled) => enabled !== false,
        createValidator: () => new TraceCornerRadiusValidator(),
        supportsBackend: true, // Needs pcbTracingGraph (provided via BackendPcbRuntimeStructures)
        requiresRuntimeStructures: true, // Needs pcbTracingGraph
    },
    ProtectedIntrusionsValidator: {
        isEnabled: (enabled) => enabled !== false,
        createValidator: () => new ProtectedIntrusionsValidator(),
        supportsBackend: true, // Needs containerMap and nodeShapesMap (provided via BackendPcbRuntimeStructures)
        requiresRuntimeStructures: true, // Needs containerMap and nodeShapesMap from PcbPrimitiveStore
    },
    // ============================================
    // ERC Validators (Schematic Electrical Checks)
    // Disabled by default, require explicit enablement via feature flag
    // These work with schematic data (elements, vertices, netMap) and can run on backend
    // ============================================
    ErcDriverConflictValidator: {
        isEnabled: (enabled) => enabled === true,
        createValidator: () => new ErcDriverConflictValidator(),
        supportsBackend: true, // Needs elements and netMap (computable from vertices)
    },
    ErcDuplicateReferenceValidator: {
        isEnabled: (enabled) => enabled === true,
        createValidator: () => new ErcDuplicateReferenceValidator(),
        supportsBackend: true, // Only needs elements
    },
    ErcFloatingPinValidator: {
        isEnabled: (enabled) => enabled === true,
        createValidator: () => new ErcFloatingPinValidator(),
        supportsBackend: true, // Needs elements and netMap
    },
    ErcNoConnectValidator: {
        isEnabled: (enabled) => enabled === true,
        createValidator: () => new ErcNoConnectValidator(),
        supportsBackend: true, // Needs elements and netMap
    },
    ErcPinToPinValidator: {
        isEnabled: (enabled) => enabled === true,
        createValidator: () => new ErcPinToPinValidator(),
        supportsBackend: true, // Needs elements and netMap
    },
    ErcShortedComponentsValidator: {
        isEnabled: (enabled) => enabled === true,
        createValidator: () => new ErcShortedComponentsValidator(),
        supportsBackend: true, // Needs elements and netMap
    },
    ErcPowerPinValidator: {
        isEnabled: (enabled) => enabled === true,
        createValidator: () => new ErcPowerPinValidator(),
        supportsBackend: true, // Needs elements and netMap
    },
    ErcSimilarLabelsValidator: {
        isEnabled: (enabled) => enabled === true,
        createValidator: () => new ErcSimilarLabelsValidator(),
        supportsBackend: true, // Needs elements and netMap
    },
    ErcSinglePinNetValidator: {
        isEnabled: (enabled) => enabled === true,
        createValidator: () => new ErcSinglePinNetValidator(),
        supportsBackend: true, // Needs elements and netMap
    },
    ErcUndrivenNetValidator: {
        isEnabled: (enabled) => enabled === true,
        createValidator: () => new ErcUndrivenNetValidator(),
        supportsBackend: true, // Needs elements and netMap
    },
    ErcDifferentUnitFootprintValidator: {
        isEnabled: (enabled) => enabled === true,
        createValidator: () => new ErcDifferentUnitFootprintValidator(),
        supportsBackend: true, // Only needs elements
    },
    ErcDifferentUnitNetValidator: {
        isEnabled: (enabled) => enabled === true,
        createValidator: () => new ErcDifferentUnitNetValidator(),
        supportsBackend: true, // Needs elements and netMap
    },
    ErcDifferentUnitValueValidator: {
        isEnabled: (enabled) => enabled === true,
        createValidator: () => new ErcDifferentUnitValueValidator(),
        supportsBackend: true, // Only needs elements
    },
    ErcSimilarLabelAndPowerValidator: {
        isEnabled: (enabled) => enabled === true,
        createValidator: () => new ErcSimilarLabelAndPowerValidator(),
        supportsBackend: true, // Needs elements and netMap
    },
    ErcSimilarPowerValidator: {
        isEnabled: (enabled) => enabled === true,
        createValidator: () => new ErcSimilarPowerValidator(),
        supportsBackend: true, // Needs elements and netMap
    },
    ErcUnannotatedValidator: {
        isEnabled: (enabled) => enabled === true,
        createValidator: () => new ErcUnannotatedValidator(),
        supportsBackend: true, // Only needs elements
    },
    ErcUnconnectedWireEndpointValidator: {
        isEnabled: (enabled) => enabled === true,
        createValidator: () => new ErcUnconnectedWireEndpointValidator(),
        supportsBackend: true, // Needs vertices
    },
    ErcWireDanglingValidator: {
        isEnabled: (enabled) => enabled === true,
        createValidator: () => new ErcWireDanglingValidator(),
        supportsBackend: true, // Needs vertices
    },
};
/**
 * Generates an array of enabled validators based on feature flag configuration.
 *
 * @param enabledCheckers - Feature flag configuration specifying which validators are enabled/disabled
 * @param validatorOverrides - Optional map of validator overrides (useful for testing)
 * @returns Array of enabled validator instances
 */
export function generateValidatorsFromFeatureFlag(enabledCheckers, validatorOverrides) {
    const validators = [];
    for (const [key, config] of Object.entries(validatorRegistry)) {
        if (!config.isEnabled(enabledCheckers?.[key])) {
            continue;
        }
        const validator = validatorOverrides?.[key] ?? config.createValidator();
        validators.push(validator);
    }
    return validators;
}
/**
 * Generates an array of enabled validators that can run on the backend.
 * Only includes validators that:
 * 1. Are enabled based on feature flags
 * 2. Support backend execution (supportsBackend: true)
 * 3. If they require runtime structures, those structures must be available
 *
 * @param enabledCheckers - Feature flag configuration specifying which validators are enabled/disabled
 * @param hasRuntimeStructures - Whether runtime structures (baked nodes, tracing graph, primitive store) are available
 * @returns Array of enabled backend-compatible validator instances
 */
export function generateBackendValidatorsFromFeatureFlag(enabledCheckers, hasRuntimeStructures = false) {
    const validators = [];
    for (const [key, config] of Object.entries(validatorRegistry)) {
        if (!config.supportsBackend) {
            continue;
        }
        // Skip validators that require runtime structures if they're not available
        if (config.requiresRuntimeStructures && !hasRuntimeStructures) {
            continue;
        }
        if (!config.isEnabled(enabledCheckers?.[key])) {
            continue;
        }
        validators.push(config.createValidator());
    }
    return validators;
}
export { validatorRegistry };
//# sourceMappingURL=validatorRegistry.js.map